import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatInTimeZone } from "date-fns-tz";
import BottomNav from "../../components/BottomNav/BottomNav";
import Calendar, { CalendarEvent } from "../../components/Calendar/Calendar";
import Table, { TableColumn } from "../../components/Table/Table";
import TopMenu from "../../components/TopMenu/TopMenu";
import { getPlaceByCode } from "../../api/PlacesApi";
import { deleteShift, getShifts } from "../../api/ShiftsApi";
import { ShiftStatus } from "../../types/shiftStatus";
import { normalizeShiftMode, ShiftMode } from "../../types/shiftMode";
import useTimezone from "../../hooks/useTimezone";
import "./PlantoesCadastrados.css";

const FALLBACK_TIMEZONE = "UTC";

type ViewMode = "mensal" | "diario";

type PlantaoRow = {
  id: string;
  titulo: string;
  dataHora: string;
  dataKey: string;
  timestamp: number;
};

type PlantaoEvent = CalendarEvent & {
  mode: ShiftMode;
  localId?: string;
};

const LOCATION_NAMES_BY_ID: Record<string, string> = {
  americana: "Americana",
  campinas: "Campinas",
  "sao-paulo": "São Paulo",
  "ribeirao-preto": "Ribeirão Preto",
  santos: "Santos",
  curitiba: "Curitiba",
  "rio-de-janeiro": "Rio de Janeiro",
  "belo-horizonte": "Belo Horizonte"
};

function parseEventDate(rawDate: string): Date | null {
  const plainDatePattern = /^\d{4}-\d{2}-\d{2}$/;

  if (plainDatePattern.test(rawDate)) {
    return new Date(`${rawDate}T00:00:00`);
  }

  const parsed = new Date(rawDate);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
}

function getMonthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function formatDate(dateKey: string): string {
  const [year, month, day] = dateKey.split("-");
  return `${day}/${month}/${year}`;
}

function getCalendarDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getDateKeyInTimezone(value: string | Date, timezone: string): string | null {
  try {
    return formatInTimeZone(value, timezone, "yyyy-MM-dd");
  } catch {
    return null;
  }
}

function formatDateTimeInTimezone(value: string | Date, timezone: string): string {
  return formatInTimeZone(value, timezone, "dd/MM/yyyy HH:mm");
}

function formatTimeInTimezone(value: string | Date, timezone: string): string {
  return formatInTimeZone(value, timezone, "HH:mm");
}

function formatDateTimeRange(startDate: string | Date, endDate: string | Date, timezone: string): string {
  const startDateKey = getDateKeyInTimezone(startDate, timezone);
  const endDateKey = getDateKeyInTimezone(endDate, timezone);

  if (startDateKey && endDateKey && startDateKey !== endDateKey) {
    return `${formatDateTimeInTimezone(startDate, timezone)} - ${formatDateTimeInTimezone(endDate, timezone)}`;
  }

  return `${formatDateTimeInTimezone(startDate, timezone)} - ${formatTimeInTimezone(endDate, timezone)}`;
}

function getErrorMessage(error: unknown): string {
  const directusMessage = (error as any)?.errors?.[0]?.message;
  if (typeof directusMessage === "string" && directusMessage.trim().length > 0) {
    return directusMessage;
  }

  const fallbackMessage = (error as any)?.message;
  if (typeof fallbackMessage === "string" && fallbackMessage.trim().length > 0) {
    return fallbackMessage;
  }

  return "Erro ao deletar plantão. Tente novamente.";
}

export default function PlantoesCadastrados() {
  const navigate = useNavigate();
  const { timezone: configuredTimezone } = useTimezone();
  const timezone = configuredTimezone || FALLBACK_TIMEZONE;
  const [searchParams] = useSearchParams();
  const [initialMonth] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [viewMode, setViewMode] = useState<ViewMode>("mensal");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [visibleMonth, setVisibleMonth] = useState<Date>(initialMonth);
  const [resolvedLocalName, setResolvedLocalName] = useState("");
  const [resolvedPlaceId, setResolvedPlaceId] = useState("");
  const [isResolvingLocalName, setIsResolvingLocalName] = useState(false);
  const [allEvents, setAllEvents] = useState<PlantaoEvent[]>([]);
  const [deletingShiftId, setDeletingShiftId] = useState<string | null>(null);

  const tipoQuery = (searchParams.get("tipo") || searchParams.get("modo") || "online").toLowerCase();
  const isLocalMode = tipoQuery === "local" || tipoQuery === "presencial";
  const mode: ShiftMode = isLocalMode ? ShiftMode.Presential : ShiftMode.Online;
  const localId = searchParams.get("localId") || searchParams.get("placeId") || "";

  useEffect(() => {
    if (!isLocalMode || !localId) {
      setResolvedLocalName("");
      setResolvedPlaceId("");
      setIsResolvingLocalName(false);
      return;
    }

    setIsResolvingLocalName(true);

    const localIdLower = localId.toLowerCase();
    const mappedName = LOCATION_NAMES_BY_ID[localIdLower];
    if (mappedName) {
      setResolvedLocalName(mappedName);
    }

    getPlaceByCode(localId)
      .then((place) => {
        const placeName = typeof (place as any)?.name === "string" ? (place as any).name : "";
        const placeId = place?.id != null ? String(place.id) : "";
        setResolvedPlaceId(placeId);
        setResolvedLocalName(placeName || "");
      })
      .catch((err) => {
        console.error("Erro ao resolver nome do local:", err);
        setResolvedPlaceId("");
        setResolvedLocalName("");
      })
      .finally(() => {
        setIsResolvingLocalName(false);
      });
  }, [isLocalMode, localId]);

  useEffect(() => {
    let cancelled = false;

    async function fetchEvents() {
      try {
        if (mode === ShiftMode.Presential && !resolvedPlaceId) {
          setAllEvents([]);
          return;
        }

        const shifts = await getShifts(
          [ShiftStatus.OPEN, ShiftStatus.PRE_OPEN],
          mode,
          mode === ShiftMode.Presential ? resolvedPlaceId : undefined
        );
        if (cancelled) {
          return;
        }

        const mappedEvents: PlantaoEvent[] = shifts.map((shift: any) => {
          const shiftMode = normalizeShiftMode(shift?.mode);
          const placeCode = typeof shift?.place_id === "object"
            ? String((shift.place_id as any)?.code ?? "")
            : "";
          const teamEntries = Array.isArray(shift?.shift_team_shift)
            ? shift.shift_team_shift
            : [];

          const teamDateTimes = teamEntries
            .map((entry: any) => ({
              startAt: String(entry?.start_date_utc ?? ""),
              endAt: String(entry?.end_date_utc ?? "")
            }))
            .filter((entry: { startAt: string; endAt: string }) => {
              const startTime = new Date(entry.startAt).getTime();
              const endTime = new Date(entry.endAt).getTime();
              return Number.isFinite(startTime) && Number.isFinite(endTime);
            });

          const startAt = teamDateTimes.length > 0
            ? teamDateTimes.reduce((earliest, current) =>
              new Date(current.startAt).getTime() < new Date(earliest.startAt).getTime()
                ? current
                : earliest
            ).startAt
            : String(shift?.start_date_utc ?? "");

          const endAt = teamDateTimes.length > 0
            ? teamDateTimes.reduce((latest, current) =>
              new Date(current.endAt).getTime() > new Date(latest.endAt).getTime()
                ? current
                : latest
            ).endAt
            : String(shift?.end_date_utc ?? "");

          return {
            id: String(shift?.id ?? ""),
            title: "Plantao Entrevista",
            startAt,
            endAt,
            mode: shiftMode,
            ...(placeCode ? { localId: placeCode } : {})
          };
        }).filter((event) => Boolean(event.id && event.startAt && event.endAt));

        setAllEvents(mappedEvents);
      } catch (err) {
        console.error("Erro ao carregar plantões cadastrados:", err);
        if (!cancelled) {
          setAllEvents([]);
        }
      }
    }

    fetchEvents();

    return () => {
      cancelled = true;
    };
  }, [mode, resolvedPlaceId]);

  const events = useMemo<CalendarEvent[]>(() => {
    if (mode === ShiftMode.Online) {
      return allEvents.filter((event) => event.mode === ShiftMode.Online);
    }

    return allEvents.filter((event) => event.mode === ShiftMode.Presential);
  }, [allEvents, mode]);

  const rows = useMemo<PlantaoRow[]>(() => {
    return events
      .map((event) => {
        const parsedStartDate = parseEventDate(event.startAt);
        const parsedEndDate = parseEventDate(event.endAt);
        const dataKey = getDateKeyInTimezone(event.startAt, timezone);

        if (!dataKey || !parsedStartDate || !parsedEndDate) {
          return null;
        }

        return {
          id: event.id,
          titulo: event.title,
          dataHora: formatDateTimeRange(event.startAt, event.endAt, timezone),
          dataKey,
          timestamp: parsedStartDate.getTime()
        };
      })
      .filter((row): row is PlantaoRow => row !== null)
      .sort((first, second) => first.timestamp - second.timestamp);
  }, [events, timezone]);

  const monthlyRows = useMemo(() => {
    const monthKey = getMonthKey(visibleMonth);
    return rows.filter((row) => row.dataKey.startsWith(`${monthKey}-`));
  }, [rows, visibleMonth]);

  const dailyRows = useMemo(() => {
    if (!selectedDate) {
      return [];
    }

    const selectedDateKey = getCalendarDateKey(selectedDate);

    return rows.filter((row) => row.dataKey === selectedDateKey);
  }, [rows, selectedDate]);

  const tableRows = viewMode === "diario" ? dailyRows : monthlyRows;
  const modeTitle = viewMode === "diario" ? "Visao diaria" : "Visao mensal";

  const modeDescription =
    viewMode === "diario"
      ? selectedDate
        ? (() => {
          const selectedDateKey = getCalendarDateKey(selectedDate);
          return `Plantoes do dia ${formatDate(selectedDateKey)}`;
        })()
        : "Selecione um dia no calendario para listar os plantoes."
      : `Plantoes de ${new Intl.DateTimeFormat("pt-BR", {
        month: "long",
        year: "numeric"
      }).format(visibleMonth)}`;

  const localNameLabel = resolvedLocalName || "Local nao informado";

  const columns: TableColumn<PlantaoRow>[] = [
    {
      id: "dataHora",
      header: "DATA / HORA",
      render: (row) => row.dataHora
    },
    {
      id: "acoes",
      header: "AÇÕES",
      render: (row) => (
        <div className="plantoes-teste-actions">
          <button
            type="button"
            className="plantoes-teste-action-button is-edit"
            onClick={() => handleEdit(row)}
            aria-label={`Editar`}
            title="Editar"
          >
            <svg viewBox="0 0 24 24" className="plantoes-teste-action-icon" aria-hidden="true">
              <path
                d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l9.06-9.06.92.92L5.92 19.58zM20.71 7.04a1.003 1.003 0 000-1.42L18.37 3.29a1.003 1.003 0 00-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.83z"
                fill="currentColor"
              />
            </svg>
          </button>

          <button
            type="button"
            className="plantoes-teste-action-button is-delete"
            onClick={() => handleDelete(row)}
            disabled={deletingShiftId === row.id}
            aria-label={`Deletar ${row.titulo}`}
            title={deletingShiftId === row.id ? "Deletando..." : "Deletar"}
          >
            <svg viewBox="0 0 24 24" className="plantoes-teste-action-icon" aria-hidden="true">
              <path
                d="M6 7h12v2H6V7zm2 3h2v9H8v-9zm6 0h2v9h-2v-9zM9 4h6l1 1h4v2H4V5h4l1-1z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
      )
    }
  ];

  function handleEdit(row: PlantaoRow) {
    navigate(`/plantao/${row.id}`);
  }

  async function handleDelete(row: PlantaoRow) {
    if (deletingShiftId) {
      return;
    }

    const confirmDelete = window.confirm(
      `Tem certeza que deseja deletar o plantao de ${row.dataHora}?`
    );

    if (!confirmDelete) {
      return;
    }

    try {
      setDeletingShiftId(row.id);
      await deleteShift(row.id);
      setAllEvents((current) => current.filter((event) => event.id !== row.id));
    } catch (err) {
      console.error("Erro ao deletar plantão:", err);
      window.alert(getErrorMessage(err));
    } finally {
      setDeletingShiftId(null);
    }
  }

  function handleDayClick(date: Date) {
    setSelectedDate(date);
    setViewMode("diario");
  }

  function handleMonthChange(month: Date) {
    setVisibleMonth(new Date(month.getFullYear(), month.getMonth(), 1));
  }

  function showMonthlyView() {
    setViewMode("mensal");
  }

  function showDailyView() {
    if (!selectedDate) {
      return;
    }

    setViewMode("diario");
  }

  function goToNovoPlantao() {
    const params = new URLSearchParams();
    params.set("tipo", mode === ShiftMode.Presential ? "presencial" : "online");

    if (selectedDate) {
      params.set("dataInicial", getCalendarDateKey(selectedDate));
    }

    if (mode === ShiftMode.Presential && localId) {
      params.set("localId", localId);
      params.set("placeId", localId);
    }

    navigate(`/plantao?${params.toString()}`);
  }

  return (
    <div className="plantoes-teste-page">
      <TopMenu />

      <main className="plantoes-teste-content">
        <section className="plantoes-teste-hero">
          <p className="plantoes-teste-title">
            {isLocalMode ? (
              <>
                Plantões Cadastrados - {isResolvingLocalName ? <span className="plantoes-teste-skeleton plantoes-teste-skeleton-title" aria-label="Carregando nome do local" /> : localNameLabel}
              </>
            ) : (
              "Plantões Cadastrados"
            )}
          </p>
          <p className="plantoes-teste-subtitle">
            {isLocalMode ? (
              <>
                Plantões de {isResolvingLocalName ? <span className="plantoes-teste-skeleton plantoes-teste-skeleton-subtitle" aria-label="Carregando nome do local" /> : localNameLabel}
              </>
            ) : (
              "Plantões Online"
            )}
          </p>
        </section>

        <Calendar
          events={events}
          initialMonth={initialMonth}
          timezone={timezone}
          selectedDate={selectedDate}
          onDayClick={handleDayClick}
          onVisibleMonthChange={handleMonthChange}
        />

        <section className="plantoes-teste-lista">
          <div className="plantoes-teste-lista-header">
            <div>
              <p className="plantoes-teste-lista-title">{modeTitle}</p>
              <p className="plantoes-teste-lista-subtitle">{modeDescription}</p>
            </div>

            <div className="plantoes-teste-lista-controls">
              <button
                type="button"
                className="plantoes-teste-add-button"
                onClick={goToNovoPlantao}
              >
                Adicionar Plantao
              </button>

              <div className="plantoes-teste-modos" role="group" aria-label="Modo de visualizacao">
                <button
                  type="button"
                  className={`plantoes-teste-modo ${viewMode === "mensal" ? "is-active" : ""}`.trim()}
                  onClick={showMonthlyView}
                >
                  Mensal
                </button>
                <button
                  type="button"
                  className={`plantoes-teste-modo ${viewMode === "diario" ? "is-active" : ""}`.trim()}
                  onClick={showDailyView}
                  disabled={!selectedDate}
                >
                  Diario
                </button>
              </div>
            </div>
          </div>

          {tableRows.length > 0 ? (
            <Table<PlantaoRow>
              bodyRowClassName="plantoes-teste-row"
              columns={columns}
              data={tableRows}
              getRowKey={(row) => row.id}
              gridTemplateColumns="minmax(220px, 1fr) minmax(98px, 120px)"
              headerRowClassName="plantoes-teste-row plantoes-teste-header"
              wrapperClassName="plantoes-teste-table-wrapper"
            />
          ) : (
            <p className="plantoes-teste-empty">Nenhum plantao para o filtro atual.</p>
          )}
        </section>
      </main>

      <BottomNav />
    </div>
  );
}
